import { ApiError } from '@/shared/api/api-client';
import type { AmbientDeclineReason } from '@scripsteam/scrips-signal-ds';
import type { AcceptOutcome } from './accept-rules';
import { ENCOUNTER_NARRATIVE_LINK_ID } from './orphan-sink';
import type { Proposal } from './ambient-types';
import { proposalDeltaId } from './reanalysis-deltas';
import type { AmbientAcceptOutcomeWire, AmbientDecisionRow, DecisionIdentity } from './decision-types';

/**
 * How a proposal is named to the decision endpoints — and how a server row is joined back to a
 * card. Pure. No network, no React.
 *
 * THE SAME IDENTITY AS `acceptKey`. `proposalDeltaId` is `"<kind> <normalised key>"`, the identity
 * the panel already uses for acceptances and re-analysis, so a decision recorded against a card
 * survives a re-analysis exactly as an acceptance does. It is split at the first space rather than
 * re-derived, so the two can never disagree about what a proposal is called.
 */

/** Server caps (`AmbientProposalDecisionService` / `AmbientChiefComplaintService`). */
const MAX_KEY = 512;
const MAX_LABEL = 512;
const MAX_TURN_ID = 64;

/** The chief complaint is not a `Proposal` kind; it is its own durable kind on the server. */
export const CHIEF_COMPLAINT_KIND = 'chief-complaint';

/**
 * PROD-3195: a dictated narrative (the HPI and any other narrative slot) is a proposal of this kind.
 * It must be an active row of the Patient service's ambient action register (action kind
 * `narrative.fill-empty`, Scrips.Patient PROD-3195), or the decision endpoints answer 400.
 */
export const NARRATIVE_KIND = 'narrative';

const clip = (value: string, max: number) => (value.length > max ? value.slice(0, max) : value);

function labelOf(proposal: Proposal): string {
  const source = proposal.source as { name?: string; modality?: string; bodyArea?: string };
  const label = source.name?.trim() || [source.modality, source.bodyArea].filter(Boolean).join(' ').trim();
  return clip(label || proposal.id, MAX_LABEL);
}

export function decisionIdentityOf(proposal: Proposal): DecisionIdentity {
  const delta = proposalDeltaId(proposal);
  const space = delta?.indexOf(' ') ?? -1;
  const proposalKind = delta && space > 0
    ? delta.slice(0, space)
    : proposal.kind === 'imaging' ? 'radiology' : proposal.kind;
  const proposalKey = delta && space > 0 ? delta.slice(space + 1) : proposal.id;
  const turnId = proposal.utterance?.turnId;
  return {
    proposalKind,
    proposalKey: clip(proposalKey, MAX_KEY),
    proposalLabel: labelOf(proposal),
    ...(turnId && turnId.length <= MAX_TURN_ID ? { turnId } : {}),
  };
}

/**
 * The dictated chief complaint as a durable proposal. Keyed on the normalised text, so the same
 * complaint dictated again in a re-analysis is the same proposal, not a second one.
 */
export function chiefComplaintIdentity(text: string): DecisionIdentity {
  const display = text.trim().replace(/\s+/g, ' ');
  return {
    proposalKind: CHIEF_COMPLAINT_KIND,
    proposalKey: clip(display.toLowerCase(), MAX_KEY),
    proposalLabel: clip(display, MAX_LABEL),
  };
}

/**
 * A dictated narrative as a durable proposal. Keyed on the slot (`narrative:<linkId>`); the label is the
 * dictated text (clipped to 512). A decision covers only the text it names: when a re-analysis gives the
 * same wording it stays hidden, and new wording for a decided slot is a fresh offer.
 */
export function narrativeIdentity(linkId: string, text: string): DecisionIdentity {
  return {
    proposalKind: NARRATIVE_KIND,
    proposalKey: clip(`narrative:${linkId}`, MAX_KEY),
    proposalLabel: clip(text, MAX_LABEL),
  };
}

/**
 * A stable short hash of a line: FNV-1a (32 bit) as 8 hex characters. The same text gives the same
 * hash in every browser and after every reload. Not a security hash: it only tells lines apart.
 */
export function shortTextHash(text: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

/**
 * PROD-3195: the decision key of one "Additional notes" line (the encounter narrative sink,
 * `orphan-sink.ts`). Format: `narrative:<sink linkId>:<shortTextHash of the trimmed line>`, for
 * example `narrative:encounter-narrative:1a2b3c4d`.
 *
 * It uses the register kind `narrative` (the one narrative kind active in the register), so no new
 * backend kind is needed. The hash keeps the key short and the same for the same line, so a
 * re-analysis or a reload that gives the same line finds its decision. Each line is its own offer.
 */
export function sinkLineKey(text: string): string {
  return clip(`narrative:${ENCOUNTER_NARRATIVE_LINK_ID}:${shortTextHash(text.trim())}`, MAX_KEY);
}

/** One "Additional notes" line as a durable proposal: `sinkLineKey`, labelled by the line (clipped to 512). */
export function sinkLineIdentity(text: string): DecisionIdentity {
  return {
    proposalKind: NARRATIVE_KIND,
    proposalKey: sinkLineKey(text),
    proposalLabel: clip(text.trim(), MAX_LABEL),
  };
}

/** The join key between a card and a server row. */
export const decisionKey = (identity: Pick<DecisionIdentity, 'proposalKind' | 'proposalKey'>) =>
  `${identity.proposalKind}|${identity.proposalKey}`;

/**
 * The host's outcome, as the server's vocabulary. Only `'written'` is `Written` — every other
 * outcome left nothing in the chart, which is exactly what `AwaitingClinician` records.
 */
export function acceptOutcomeWire(outcome: AcceptOutcome): AmbientAcceptOutcomeWire {
  return outcome === 'written' ? 'Written' : 'AwaitingClinician';
}

export const declineReasonCode = (reason: AmbientDeclineReason): string => reason;

/**
 * The CURRENT decision per proposal, from the full history: the latest row per key, and a key
 * whose latest row is `Undone` is back to undecided — present in the map as that Undone row so a
 * caller can tell "undone" from "never decided", but never counted as accepted or declined.
 */
export function currentDecisions(
  history: readonly AmbientDecisionRow[] | undefined,
): ReadonlyMap<string, AmbientDecisionRow> {
  const latest = new Map<string, AmbientDecisionRow>();
  const ordered = [...(history ?? [])].sort(
    (a, b) => Date.parse(b.decidedAt) - Date.parse(a.decidedAt),
  );
  for (const row of ordered) {
    const key = decisionKey(row);
    if (!latest.has(key)) latest.set(key, row);
  }
  return latest;
}

/**
 * What the server said when it refused, in a sentence the clinician can act on.
 *
 * The server answers with a JSON string (400 / 404 / 409) or `{ error, writeKind }` (the 403
 * prescribing gate). Neither carries patient content — the server guarantees that — so the text is
 * safe to render. It is never logged.
 */
export function refusalMessage(error: unknown): string {
  if (error instanceof ApiError) {
    let detail = '';
    try {
      const parsed: unknown = JSON.parse(error.body);
      if (typeof parsed === 'string') detail = parsed;
      else if (parsed && typeof parsed === 'object') {
        const obj = parsed as { error?: unknown; title?: unknown; detail?: unknown };
        detail = String(obj.error ?? obj.detail ?? obj.title ?? '');
      }
    } catch {
      detail = error.body;
    }
    const prefix =
      error.status === 403 ? 'Not permitted for your role'
        : error.status === 409 ? 'Refused — the record has moved on'
          : error.status === 404 ? 'Not found for this patient'
            : error.status === 400 ? 'Rejected as invalid'
              : `Server error ${error.status}`;
    return detail.trim() ? `${prefix}: ${detail.trim()}` : prefix;
  }
  return 'The request did not reach the server. Nothing was recorded.';
}
